import { ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { type Arch, DEFAULT_ARCH, Platform } from '@/lib/platform';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { ButtonGroup } from './ButtonGroup';
import type { releases } from '@kirin/api-client/contracts';
import { InstallerHashDisplay } from './InstallTab/InstallerHashDisplay';
import { filenameFromUrl } from './InstallTab/installerHashes';

export interface PlatformDownloadMeta {
  platform: Platform | null;
  version: string;
  url: string;
  /**
   * Optional architecture qualifier. Used by Linux and Windows (amd64 /
   * arm64). macOS entries leave this undefined — it ships a universal binary.
   * When set, two metas can share the same platform; the matcher picks the
   * one matching userArch (if known) or `DEFAULT_ARCH` (if not).
   */
  arch?: Arch;
  /**
   * Linux package format. When set, this entry represents a native package
   * (deb/rpm) rather than a bare binary. Package entries are rendered through
   * InstallTrigger via `agentPlatformDownloadMeta` alongside binary entries.
   */
  format?: 'deb' | 'rpm';
  /**
   * KIR-4071: SHA-256 of this exact installer file, for EDR allow-listing.
   * Shown beneath the download button when present; absent → nothing renders
   * (fail-safe, until the producer KIR-4558 publishes hashes to the manifest).
   */
  sha256?: string;
}

export type InstallEnvironment =
  | 'Kirin Agent'
  | 'Cursor'
  | 'VSCode Copilot'
  | 'Claude Code'
  | 'Claude Desktop'
  | 'Devin Desktop';

interface InstallTriggerProps {
  environment: InstallEnvironment;
  userPlatform: Platform | null;
  /**
   * Optional architecture for the visitor (Linux / Windows). When set, the
   * primary download button prefers a meta whose `arch` matches. When
   * unset on a multi-arch platform, the matcher falls back to amd64 (the
   * safe default; see DEFAULT_ARCH).
   */
  userArch?: Arch | null;
  platformDownloadMeta: PlatformDownloadMeta[];
  fallbackDownloadMeta?: PlatformDownloadMeta;
  /** Override the auto-generated label for a given platform download */
  formatLabel?: (meta: PlatformDownloadMeta) => string;
  /**
   * KIR-4071: the full published manifest. When the primary button carries a
   * `sha256`, its {@link InstallerHashDisplay} uses this to power the "View all
   * checksums" popout (every OS's installer, all versions). Absent → no hashes.
   */
  installerHashes?: releases.InstallerHashes | null;
}

const getPlatformLabel = (platform: Platform | null): string => {
  switch (platform) {
    case Platform.DARWIN:
      return 'macOS';
    case Platform.WIN32:
      return 'Windows 11+';
    case Platform.LINUX:
      return 'Linux';
    default:
      return '';
  }
};

const getPlatformDownloadLabel = (
  meta: PlatformDownloadMeta | null,
  environment: InstallEnvironment,
  formatLabel?: (meta: PlatformDownloadMeta) => string
): string => {
  if (!meta) return '';
  if (formatLabel) return formatLabel(meta);
  const platformLabel = getPlatformLabel(meta.platform);
  const archSuffix = meta.arch ? ` (${meta.arch})` : '';
  return `Download Kirin ${environment} ${meta.version}${platformLabel ? ` for ${platformLabel}${archSuffix}` : ''}`;
};

/**
 * Pick the meta that should drive the primary download button when the
 * visitor's platform matches one or more entries. Order of preference:
 *   1. (platform, arch) exact match against userArch.
 *   2. If userPlatform is multi-arch (Linux / Windows) and no exact match,
 *      the entry whose arch is DEFAULT_ARCH (amd64) — the safe default.
 *   3. Any entry for the platform that has a URL (handles single-arch
 *      platforms like macOS).
 *   4. Any entry for the platform without a URL (the "coming soon"
 *      placeholder kept for label generation).
 */
const selectPrimaryMeta = (
  metas: PlatformDownloadMeta[],
  userPlatform: Platform | null,
  userArch: Arch | null | undefined
): { meta: PlatformDownloadMeta | null; comingSoon: boolean } => {
  const sameOS = metas.filter(m => m.platform === userPlatform);
  if (sameOS.length === 0) {
    return { meta: null, comingSoon: false };
  }

  const downloadable = sameOS.filter(m => m.url);

  if (userArch) {
    const exact = downloadable.find(m => m.arch === userArch);
    if (exact) return { meta: exact, comingSoon: false };
  }

  // Multi-arch platforms (Linux / Windows): fall back to the amd64 entry when
  // arch is unknown. amd64 runs on ARM64 Windows via emulation, so it is the
  // safe default (never offer an arm64 build we can't be sure will run).
  if (userPlatform === Platform.LINUX || userPlatform === Platform.WIN32) {
    const defaultArch = downloadable.find(m => m.arch === DEFAULT_ARCH);
    if (defaultArch) return { meta: defaultArch, comingSoon: false };
  }

  if (downloadable.length > 0) {
    return { meta: downloadable[0], comingSoon: false };
  }

  return { meta: sameOS[0], comingSoon: true };
};

export const InstallTrigger = ({
  environment,
  userPlatform,
  userArch,
  platformDownloadMeta,
  fallbackDownloadMeta,
  formatLabel,
  installerHashes,
}: InstallTriggerProps) => {
  const { meta: primaryMeta, comingSoon: isComingSoon } = selectPrimaryMeta(
    platformDownloadMeta,
    userPlatform,
    userArch
  );

  const actualPlatformMeta = !isComingSoon ? primaryMeta : null;
  const comingSoonPlatformMeta = isComingSoon ? primaryMeta : null;

  const comingSoonLabel = comingSoonPlatformMeta
    ? getPlatformDownloadLabel(comingSoonPlatformMeta, environment, formatLabel)
    : '';

  const actualPlatformLabel = getPlatformDownloadLabel(
    actualPlatformMeta ?? null,
    environment,
    formatLabel
  );

  const fallbackLabel = getPlatformDownloadLabel(
    fallbackDownloadMeta || null,
    environment,
    formatLabel
  );

  // The dropdown shows every download-with-URL that ISN'T the primary
  // selection — including other architectures of the same platform (so a
  // Linux amd64 visitor can grab arm64 from the chevron, and vice-versa).
  const otherPlatformsMeta = platformDownloadMeta.filter(
    meta => meta !== actualPlatformMeta && meta.url
  );

  const hasMultiplePlatforms = otherPlatformsMeta.length > 0;
  const defaultDownloadUrl = actualPlatformMeta
    ? actualPlatformMeta.url
    : fallbackDownloadMeta?.url;

  return (
    <div className="w-full">
      <Popover>
        <PopoverAnchor>
          <ButtonGroup
            className={`border w-full gap-0 items-center rounded-md flex ${
              !hasMultiplePlatforms
                ? 'bg-foreground text-primary-foreground'
                : 'bg-background'
            }`}
          >
            {isComingSoon ? (
              <Button variant="ghost" className="w-full rounded-none" disabled>
                {comingSoonLabel}
              </Button>
            ) : (
              <Button asChild variant="ghost" className="w-full rounded-none">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={defaultDownloadUrl}
                  download
                  className="flex-1 h-full flex items-center justify-center"
                >
                  {actualPlatformLabel || fallbackLabel}
                </a>
              </Button>
            )}
            {hasMultiplePlatforms && (
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-12 h-full flex rounded-none border-l items-center justify-center cursor-pointer"
                  aria-label="Open links"
                >
                  <ChevronDown className="h-6 w-6" />
                </Button>
              </PopoverTrigger>
            )}
          </ButtonGroup>
        </PopoverAnchor>

        <PopoverContent
          align="end"
          className="w-[var(--radix-popper-anchor-width)] p-0 border-none shadow-none flex flex-col gap-1.5"
        >
          {otherPlatformsMeta.map(meta => (
            <Button
              asChild
              key={`${meta.platform ?? 'unknown'}-${meta.arch ?? 'default'}-${meta.format ?? 'default'}`}
              variant={'outline'}
              className="w-full border"
            >
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={meta.url}
                download
                className="w-full text-center pr-10"
              >
                {getPlatformDownloadLabel(meta, environment, formatLabel)}
              </a>
            </Button>
          ))}
        </PopoverContent>
      </Popover>

      {installerHashes && (
        <InstallerHashDisplay
          sha256={actualPlatformMeta?.sha256}
          filename={
            actualPlatformMeta?.url
              ? (filenameFromUrl(actualPlatformMeta.url) ?? undefined)
              : undefined
          }
          hashes={installerHashes}
        />
      )}
    </div>
  );
};
